import { useCallback } from 'react';

import { getBooleanFF } from '@atlaskit/platform-feature-flags/getBooleanFF';

import { fg } from '@confluence/feature-gating';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';

// These flags are no longer used in confluence and can be removed from this array entirely after we confirm that have been removed from the whiteboards side too
const ARCHIVED_FEATURE_FLAGS = [
	'headerV1',
	'headerV2',
	'whiteboardArchive',
	'removeWhiteboardsHome',
	'removeWhiteboardsHomeInSideNav',
	'whiteboardCreation',
	'whiteboardsEnabled',
	'whiteboardBifrost',
	'whiteboardVersionHistory',
	'digitalTeammate',
] as const;
type ArchivedFeatureFlags = (typeof ARCHIVED_FEATURE_FLAGS)[number];
type ActiveFeatureGates = 'whiteboardSolidHeader' | 'whiteboardOptOut' | 'aiFirstCreation';

export type WhiteboardFeatures = ArchivedFeatureFlags | ActiveFeatureGates;

export const useIsWhiteboardFeatureEnabled = (): {
	platformFeatureFlagResolver: typeof getBooleanFF;
	isWhiteboardFeatureEnabled: (featureName: ArchivedFeatureFlags | ActiveFeatureGates) => boolean;
	isWhiteboardCreationDropdownEnabled: () => boolean;
	isWhiteboardGAOnboardingEnabled: () => boolean;
} => {
	const isWhiteboardsEnabledInEnv = isContentTypeEnabledInCurrentEnv('whiteboard');

	const isWhiteboardFeatureEnabled = useCallback(
		(featureName: ArchivedFeatureFlags | ActiveFeatureGates): boolean => {
			if (featureName === 'whiteboardsEnabled') {
				return isWhiteboardsEnabledInEnv;
			}

			// If the FF has been cleaned up on our end just return true
			if (ARCHIVED_FEATURE_FLAGS.some((feature) => feature === featureName)) {
				return true;
			}

			// Use static feature gate calls to satisfy confluence-feature-gating/static-feature-gates
			switch (featureName) {
				case 'whiteboardSolidHeader':
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					return fg('confluence_frontend_solid_whiteboard_header');
				case 'whiteboardOptOut':
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					return fg('whiteboards_compliance_opt_out');
				case 'aiFirstCreation':
					return true;
				default:
					return false;
			}
		},
		[isWhiteboardsEnabledInEnv],
	);

	const isWhiteboardCreationDropdownEnabled = useCallback((): boolean => {
		return (
			isWhiteboardFeatureEnabled('whiteboardsEnabled') &&
			!isWhiteboardFeatureEnabled('whiteboardOptOut')
		);
	}, [isWhiteboardFeatureEnabled]);

	const isWhiteboardGAOnboardingEnabled = useCallback((): boolean => {
		return (
			isWhiteboardFeatureEnabled('whiteboardsEnabled') &&
			!isWhiteboardFeatureEnabled('whiteboardOptOut')
		);
	}, [isWhiteboardFeatureEnabled]);

	return {
		platformFeatureFlagResolver: getBooleanFF,
		isWhiteboardFeatureEnabled,
		isWhiteboardCreationDropdownEnabled,
		isWhiteboardGAOnboardingEnabled,
	};
};
